import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Fingerprint, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { RouteTransitionLogo } from '../components/RouteTransitionLogo';
import { completeLogin } from '../utils/postLoginRouting';
import {
  hasLocalPasskeyFlag,
  isPasskeySupportedOnThisDevice,
  setLocalPasskeyFlag,
  unlockWithPasskey,
} from '../utils/passkeys';
import { markSessionAlive } from '../utils/sessionGuard';
import { isFreshLogin, consumeFreshLogin } from '../utils/authStorage';
import { isNativeOrInstalledApp } from '../utils/runtimeEnv';
import { consumePendingPushTapRoute } from '../utils/fcmPush';

const FONT = "'Inria Serif', serif";
const SUB_FONT = "'Manrope', sans-serif";
const BRAND = '#703FF6';
const BRAND_DARK = '#4A20C9';

/**
 * UnlockPage — single-tap biometric re-auth screen.
 *
 * Shown on every COLD launch when the user previously enrolled a
 * passkey on this device (Revolut/Wise pattern). Resuming the app from
 * background does NOT route here — see `sessionGuard.js`.
 *
 * Behaviour:
 *  - Auto-fires `unlockWithPasskey()` on first mount so the OS prompt
 *    pops immediately. If the user cancels, they can retry or fall back
 *    to email + Google sign-in.
 *  - On success, calls `completeLogin(...)` so the post-login routing
 *    (remembered role, deep links, 2FA) behaves identically to the
 *    normal sign-in path.
 */
export default function UnlockPage({ onLogin }) {
  const navigate = useNavigate();
  const [supported, setSupported] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const tryUnlock = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { access_token: accessToken, user } = await unlockWithPasskey();
      markSessionAlive();
      await completeLogin({ accessToken, user, onLogin, navigate });
    } catch (err) {
      const errName = String(err?.name || '');
      const detail = err?.response?.data?.detail || '';
      // Spec-defined cancel codes — friendlier copy.
      if (/NotAllowedError|aborted|cancel/i.test(errName)) {
        setError('Cancelled. Tap "Unlock" to try again.');
      } else if (/no.*registered|not registered/i.test(detail)) {
        // The local flag claimed a passkey existed but the server has none —
        // clear the flag so future cold-launches go to /login instead.
        setLocalPasskeyFlag(false);
        setError('No passkey for this device. Sign in with your email or Google instead.');
      } else {
        setError(detail || err?.message || 'Could not unlock. Try again or sign in.');
      }
    } finally {
      setBusy(false);
    }
  }, [busy, navigate, onLogin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Never enforce biometric unlock outside the native app / installed
      // PWA. A plain browser tab that somehow lands on /unlock (bookmark,
      // back button after logout, deep link) must NOT pop the OS passkey
      // picker — reported bug 2026-02.
      if (!isNativeOrInstalledApp()) {
        const token = window.localStorage.getItem('token');
        const stored = window.localStorage.getItem('user');
        try {
          const parsed = stored ? JSON.parse(stored) : null;
          if (token && parsed) {
            markSessionAlive();
            onLogin?.(parsed);
            // A buffered FCM push-tap route (e.g. the tipping deep link)
            // beats the generic default-route redirect so the user lands
            // on the exact page the notification was pointing at.
            const pendingPushRoute = consumePendingPushTapRoute();
            if (pendingPushRoute) {
              navigate(pendingPushRoute, { replace: true });
              return;
            }
            const { getDefaultRoute } = await import('../utils/defaultRoute');
            navigate(getDefaultRoute(parsed) || '/', { replace: true });
          } else {
            navigate('/welcome', { replace: true });
          }
        } catch {
          navigate('/welcome', { replace: true });
        }
        return;
      }

      // If the user JUST completed sign-in (email/Google/2FA) within the
      // last minute, DO NOT ask them to biometric on top. They already
      // proved who they are seconds ago — double-gating them here was the
      // "random-emails popup after Google sign-in" confusion reported by
      // the user. Consume the flag and route them straight to their
      // default home.
      if (isFreshLogin()) {
        consumeFreshLogin();
        markSessionAlive();
        try {
          const stored = window.localStorage.getItem('user');
          const parsed = stored ? JSON.parse(stored) : null;
          if (parsed) onLogin?.(parsed);
        } catch { /* noop */ }
        // Honour any buffered push-tap route before falling through to
        // the per-role default home.
        const pendingPushRoute = consumePendingPushTapRoute();
        if (pendingPushRoute) {
          navigate(pendingPushRoute, { replace: true });
          return;
        }
        const { getDefaultRoute } = await import('../utils/defaultRoute');
        try {
          const stored = window.localStorage.getItem('user');
          const parsed = stored ? JSON.parse(stored) : null;
          navigate(getDefaultRoute(parsed) || '/', { replace: true });
        } catch {
          navigate('/', { replace: true });
        }
        return;
      }

      const ok = await isPasskeySupportedOnThisDevice();
      if (cancelled) return;
      if (!ok || !hasLocalPasskeyFlag()) {
        // Defensive: if the user navigated here directly without a
        // registered passkey (or on an unsupported device), bounce to
        // the password flow instead of leaving them stranded.
        setLocalPasskeyFlag(false);
        navigate('/login', { replace: true });
        return;
      }
      setSupported(true);
      // Auto-trigger the OS prompt on first paint — feels exactly like
      // opening Revolut.
      tryUnlock();
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const goToPasswordSignIn = () => {
    navigate('/login', { replace: true });
  };

  if (supported === null) {
    return (
      <div
        data-testid="unlock-page-loading"
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--app-bg)',
          fontFamily: SUB_FONT,
          color: 'var(--text-2)',
        }}
      >
        Checking device…
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
      data-testid="unlock-page"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        background: 'var(--app-bg)',
        backgroundImage:
          'radial-gradient(ellipse 600px 360px at 50% -140px, rgba(112, 63, 246, 0.20) 0%, rgba(112, 63, 246, 0) 60%)',
        padding: '0 24px 28px',
      }}
    >
      <RouteTransitionLogo
        width={86}
        height={76}
        dataTestId="unlock-page-logo"
        style={{ marginTop: 64 }}
      />
      <h1
        style={{
          fontFamily: FONT,
          fontSize: 26,
          fontWeight: 400,
          color: 'var(--text)',
          margin: '14px 0 4px',
          textAlign: 'center',
        }}
      >
        Welcome back
      </h1>
      <p
        style={{
          margin: 0,
          fontFamily: SUB_FONT,
          fontSize: 13.5,
          color: 'var(--text-2)',
          textAlign: 'center',
          maxWidth: 320,
          lineHeight: 1.45,
        }}
      >
        Unlock Allure with your fingerprint, Face ID or device PIN.
      </p>

      <button
        onClick={tryUnlock}
        disabled={busy}
        data-testid="unlock-page-unlock-button"
        style={{
          marginTop: 40,
          width: 116,
          height: 116,
          borderRadius: '50%',
          border: 'none',
          background: busy
            ? 'linear-gradient(135deg, rgba(112,63,246,0.55) 0%, rgba(74,32,201,0.55) 100%)'
            : `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
          color: '#FFFFFF',
          cursor: busy ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 18px 36px rgba(112, 63, 246, 0.36)',
          transition: 'transform 160ms ease, box-shadow 160ms ease',
        }}
        onMouseEnter={(e) => {
          if (!busy) e.currentTarget.style.transform = 'scale(1.04)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <Fingerprint size={48} strokeWidth={1.6} />
      </button>

      <p
        style={{
          margin: '18px 0 0',
          fontFamily: SUB_FONT,
          fontSize: 13,
          fontWeight: 700,
          color: BRAND_DARK,
          letterSpacing: '0.02em',
        }}
      >
        {busy ? 'Waiting for your device…' : 'Tap to unlock'}
      </p>

      {error && (
        <p
          data-testid="unlock-page-error"
          style={{
            marginTop: 14,
            fontFamily: SUB_FONT,
            fontSize: 12.5,
            color: '#C93636',
            textAlign: 'center',
            maxWidth: 300,
            lineHeight: 1.45,
          }}
        >
          {error}
        </p>
      )}

      <button
        onClick={goToPasswordSignIn}
        data-testid="unlock-page-fallback-button"
        style={{
          marginTop: 'auto',
          padding: '12px 18px',
          borderRadius: 999,
          border: '1px solid var(--card-border)',
          background: 'var(--surface)',
          color: 'var(--accent-text-soft)',
          fontFamily: SUB_FONT,
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <KeyRound size={14} strokeWidth={2.2} />
        Sign in with email or Google
      </button>
    </motion.div>
  );
}
